import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Upload, Building2, Search, CheckCircle, XCircle, Loader2,
  Plus, Pencil, Trash2, Save, X, Image, RefreshCw, AlertCircle,
  FileCheck, ChevronDown, ChevronUp, Eye, Download, BarChart3, Shield,
  List, Target, Users, Database, Globe
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PAPPERS_API_KEY = '753e4e90c12f4863cd69731f5086a49a518f3e4e6e415460';

// Tous les types de rapports disponibles
const AVAILABLE_REPORT_TYPES = [
  { id: 'executive', name: 'Rapport Exécutif', icon: BarChart3, color: 'bg-blue-500', description: 'Synthèse stratégique pour le management' },
  { id: 'technical', name: 'Rapport Technique Détaillé', icon: Shield, color: 'bg-green-500', description: 'Analyse complète avec CVE, CVSS, preuves' },
  { id: 'vulnerability_list', name: 'Liste de Vulnérabilités', icon: List, color: 'bg-yellow-500', description: 'Export pour outils de ticketing' },
  { id: 'redteam', name: 'Rapport Équipe Rouge / Attaque', icon: Target, color: 'bg-red-500', description: 'Scénario narratif avec kill chain' },
  { id: 'retest', name: 'Rapport de Re-test', icon: RefreshCw, color: 'bg-purple-500', description: 'Vérification des corrections' },
  { id: 'multi_campaign', name: 'Synthèse Multi-Campagnes', icon: BarChart3, color: 'bg-cyan-500', description: 'Évolution dans le temps' },
  { id: 'active_directory', name: 'Rapport Active Directory & Identités', icon: Users, color: 'bg-indigo-500', description: 'Audit AD, IAM, privilèges, GPO' },
  { id: 'data_leak', name: 'Rapport Fuite de Données / Exposition', icon: Database, color: 'bg-pink-500', description: 'DLP, fuites de credentials, fichiers sensibles' },
  { id: 'attack_surface', name: 'Rapport Exposition Internet & Surface d\'Attaque', icon: Globe, color: 'bg-orange-500', description: 'ASM, services exposés, sous-domaines' },
];

const ReportsConfig = () => {
  const [reportTypes, setReportTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [editingReport, setEditingReport] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [expandedReports, setExpandedReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Client management
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', siren: '', logo: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  // Load data from API
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [reportTypesRes, clientsRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/settings/report-types`, { headers }),
        fetch(`${API_BASE}/api/v1/settings/clients`, { headers })
      ]);

      if (reportTypesRes.ok) {
        const reportTypesData = await reportTypesRes.json();
        setReportTypes(reportTypesData);
      }

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save report types to API
  const saveReportTypes = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/v1/settings/report-types/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ report_types: reportTypes })
      });

      if (response.ok) {
        const result = await response.json();
        setReportTypes(result.report_types);
        setHasChanges(false);
      } else {
        console.error('Failed to save report types');
      }
    } catch (err) {
      console.error('Failed to save report types:', err);
    } finally {
      setSaving(false);
    }
  };

  // Save clients to API
  const saveClients = async (clientsList) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/v1/settings/clients/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ clients: clientsList })
      });

      if (response.ok) {
        const result = await response.json();
        setClients(result.clients);
      }
    } catch (err) {
      console.error('Failed to save clients:', err);
    }
  };

  // Search company via Pappers API
  const searchPappers = async (query) => {
    if (!query || query.length < 2) return;

    setSearching(true);
    setSearchResults([]);

    try {
      const response = await fetch(
        `https://api.pappers.fr/v2/recherche?api_token=${PAPPERS_API_KEY}&q=${encodeURIComponent(query)}&par_page=5`
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.resultats || []);
      } else {
        console.error('Pappers API error:', response.status);
      }
    } catch (err) {
      console.error('Pappers search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  // Verify company via Pappers API (by SIREN)
  const verifyCompany = async (siren) => {
    if (!siren || siren.length < 9) {
      setVerificationResult({ valid: false, message: 'SIREN invalide (9 chiffres requis)' });
      return;
    }

    setVerifying(true);
    setVerificationResult(null);

    try {
      const response = await fetch(
        `https://api.pappers.fr/v2/entreprise?api_token=${PAPPERS_API_KEY}&siren=${siren}`
      );

      if (response.ok) {
        const data = await response.json();
        setVerificationResult({
          valid: true,
          message: 'Entreprise verifiee',
          data: {
            nom: data.nom_entreprise || data.denomination,
            siren: data.siren,
            siret: data.siege?.siret,
            adresse: data.siege?.adresse_ligne_1,
            ville: `${data.siege?.code_postal} ${data.siege?.ville}`,
            forme_juridique: data.forme_juridique,
            capital: data.capital,
            date_creation: data.date_creation,
            effectif: data.effectif,
            activite: data.objet_social || data.domaine_activite
          }
        });

        // Auto-fill client name
        if (data.nom_entreprise || data.denomination) {
          setNewClient(prev => ({
            ...prev,
            name: data.nom_entreprise || data.denomination
          }));
        }
      } else if (response.status === 404) {
        setVerificationResult({ valid: false, message: 'Entreprise non trouvée' });
      } else {
        setVerificationResult({ valid: false, message: `Erreur API (${response.status})` });
      }
    } catch (err) {
      console.error('Pappers verification failed:', err);
      setVerificationResult({ valid: false, message: 'Erreur de connexion a l\'API Pappers' });
    } finally {
      setVerifying(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewClient(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Add client
  const addClient = async () => {
    if (!newClient.name) return;

    const client = {
      id: Date.now().toString(),
      name: newClient.name,
      siren: newClient.siren || null,
      logo: newClient.logo || null,
      verified: verificationResult?.valid || false,
      companyData: verificationResult?.data || null,
      createdAt: new Date().toISOString()
    };

    // Save to API
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/v1/settings/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: client.name,
          siren: client.siren,
          logo: client.logo,
          verified: client.verified,
          company_data: client.companyData
        })
      });

      if (response.ok) {
        // Reload clients from API
        loadData();
      } else {
        console.error('Failed to add client');
      }
    } catch (err) {
      console.error('Failed to add client:', err);
    }

    setNewClient({ name: '', siren: '', logo: null });
    setShowAddClient(false);
    setVerificationResult(null);
    setSearchResults([]);
    setSearchQuery('');
  };

  // Delete client
  const deleteClient = async (clientId) => {
    if (window.confirm('Supprimer ce client ?')) {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE}/api/v1/settings/clients/${clientId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          setClients(prev => prev.filter(c => c.id !== clientId));
        } else {
          console.error('Failed to delete client');
        }
      } catch (err) {
        console.error('Failed to delete client:', err);
      }
    }
  };

  // Toggle report expansion
  const toggleReportExpansion = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  // Update section
  const updateSection = (reportId, sectionId, updates) => {
    setReportTypes(prev => prev.map(report => {
      if (report.id === reportId) {
        return {
          ...report,
          sections: report.sections.map(section =>
            section.id === sectionId ? { ...section, ...updates } : section
          )
        };
      }
      return report;
    }));
    setHasChanges(true);
  };

  // Toggle section enabled (with auto-save)
  const toggleSection = async (reportId, sectionId) => {
    const newReportTypes = reportTypes.map(report => {
      if (report.id === reportId) {
        return {
          ...report,
          sections: report.sections.map(section =>
            section.id === sectionId ? { ...section, enabled: !section.enabled } : section
          )
        };
      }
      return report;
    });

    setReportTypes(newReportTypes);

    // Auto-save after toggle
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/v1/settings/report-types/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ report_types: newReportTypes })
      });

      if (response.ok) {
        const result = await response.json();
        setReportTypes(result.report_types);
        setHasChanges(false);
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setHasChanges(true);
    } finally {
      setSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
        <span className="ml-3 text-gray-400">Chargement des paramètres...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Save Button for Report Types */}
      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-yellow-400">
            <AlertCircle className="w-5 h-5" />
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <button
            onClick={saveReportTypes}
            disabled={saving}
            className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/80 disabled:opacity-50 font-medium flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder les rapports
          </button>
        </div>
      )}

      {/* Clients Section */}
      <div className="bg-brand-black rounded-xl border border-blue-500/30 overflow-hidden">
        <div className="p-4 border-b border-blue-500/20 bg-blue-500/5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Clients
              <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                {clients.length} client(s)
              </span>
            </h3>
            <button
              onClick={() => setShowAddClient(true)}
              className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/80 flex items-center gap-2 font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              Ajouter Client
            </button>
          </div>
        </div>

        <div className="p-4">
          {clients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun client configure</p>
              <p className="text-sm">Ajoutez un client pour personnaliser vos rapports</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map(client => (
                <div key={client.id} className="bg-brand-black-light rounded-lg p-4 border border-gray-700 hover:border-blue-500/50 transition-colors">
                  <div className="flex items-start gap-3">
                    {client.logo ? (
                      <img src={client.logo} alt={client.name} className="w-16 h-16 rounded-lg object-contain bg-white p-1" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-700 flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-white truncate">{client.name}</h4>
                        {client.verified && (
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" title="Verifie Pappers" />
                        )}
                      </div>
                      {client.siren && (
                        <p className="text-sm text-gray-400">SIREN: {client.siren}</p>
                      )}
                      {client.companyData && (
                        <p className="text-xs text-gray-500 truncate">{client.companyData.ville}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-700">
                    <button
                      onClick={() => deleteClient(client.id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddClient && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-brand-black-light border border-brand-green/30 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-6 h-6 text-brand-green" />
                Ajouter un Client
              </h3>
              <button onClick={() => { setShowAddClient(false); setVerificationResult(null); setSearchResults([]); }} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Search Pappers */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rechercher une entreprise (Pappers)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Nom de l'entreprise..."
                    className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
                  />
                  <button
                    onClick={() => searchPappers(searchQuery)}
                    disabled={searching || !searchQuery}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-brand-black rounded-lg border border-gray-700 max-h-48 overflow-y-auto">
                    {searchResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setNewClient(prev => ({
                            ...prev,
                            name: result.nom_entreprise,
                            siren: result.siren
                          }));
                          setSearchResults([]);
                          setSearchQuery('');
                          verifyCompany(result.siren);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-brand-black-lighter border-b border-gray-700 last:border-0"
                      >
                        <div className="font-medium text-white">{result.nom_entreprise}</div>
                        <div className="text-xs text-gray-400">SIREN: {result.siren} | {result.siege?.ville}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-700 my-4"></div>

              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom du client *
                </label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nom de l'entreprise"
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
                />
              </div>

              {/* SIREN */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  SIREN (optionnel - pour verification Pappers)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newClient.siren}
                    onChange={(e) => setNewClient(prev => ({ ...prev, siren: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                    placeholder="123456789"
                    maxLength={9}
                    className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green font-mono"
                  />
                  <button
                    onClick={() => verifyCompany(newClient.siren)}
                    disabled={verifying || !newClient.siren || newClient.siren.length < 9}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Vérifier
                  </button>
                </div>
              </div>

              {/* Verification Result */}
              {verificationResult && (
                <div className={`p-4 rounded-lg border ${verificationResult.valid ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {verificationResult.valid ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={verificationResult.valid ? 'text-green-400' : 'text-red-400'}>
                      {verificationResult.message}
                    </span>
                  </div>
                  {verificationResult.data && (
                    <div className="text-sm text-gray-300 space-y-1 mt-3">
                      <p><span className="text-gray-500">Nom:</span> {verificationResult.data.nom}</p>
                      <p><span className="text-gray-500">SIRET:</span> {verificationResult.data.siret}</p>
                      <p><span className="text-gray-500">Adresse:</span> {verificationResult.data.adresse}, {verificationResult.data.ville}</p>
                      <p><span className="text-gray-500">Forme:</span> {verificationResult.data.forme_juridique}</p>
                      {verificationResult.data.capital && (
                        <p><span className="text-gray-500">Capital:</span> {verificationResult.data.capital?.toLocaleString()} EUR</p>
                      )}
                      {verificationResult.data.effectif && (
                        <p><span className="text-gray-500">Effectif:</span> {verificationResult.data.effectif}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Logo (optionnel)
                </label>
                <div className="flex items-center gap-4">
                  {newClient.logo ? (
                    <div className="relative">
                      <img src={newClient.logo} alt="Logo preview" className="w-20 h-20 rounded-lg object-contain bg-white p-1" />
                      <button
                        onClick={() => setNewClient(prev => ({ ...prev, logo: null }))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-brand-green transition-colors">
                      <Upload className="w-6 h-6 text-gray-500" />
                      <span className="text-xs text-gray-500 mt-1">Upload</span>
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                  )}
                  <div className="text-sm text-gray-500">
                    <p>Formats: PNG, JPG, SVG</p>
                    <p>Taille recommandee: 200x200px</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => { setShowAddClient(false); setVerificationResult(null); setSearchResults([]); }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Annuler
                </button>
                <button
                  onClick={addClient}
                  disabled={!newClient.name}
                  className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/80 disabled:opacity-50 font-medium flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Ajouter Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Types Section */}
      <div className="bg-brand-black rounded-xl border border-purple-500/30 overflow-hidden">
        <div className="p-4 border-b border-purple-500/20 bg-purple-500/5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Types de Rapports & Configuration
            <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
              {AVAILABLE_REPORT_TYPES.length} types disponibles
            </span>
          </h3>
          <p className="text-sm text-gray-400 mt-1">Personnalisez le contenu et les sections de chaque type de rapport</p>
        </div>

        <div className="p-4 space-y-4">
          {AVAILABLE_REPORT_TYPES.map(availableType => {
            const report = reportTypes.find(r => r.id === availableType.id);
            const IconComponent = availableType.icon;

            return (
            <div key={availableType.id} className="bg-brand-black-light rounded-lg border border-gray-700">
              {/* Report Header */}
              <button
                onClick={() => report && toggleReportExpansion(report.id)}
                className={`w-full p-4 flex items-center justify-between transition-colors ${report ? 'hover:bg-brand-black' : 'opacity-60 cursor-default'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${availableType.color}/20`}>
                    <IconComponent className={`w-5 h-5 ${availableType.color.replace('bg-', 'text-').replace('-500', '-400')}`} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-white">{availableType.name}</h4>
                    <p className="text-sm text-gray-400">{availableType.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {report && report.sections ? (
                    <>
                      <span className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded">
                        {report.sections.filter(s => s.enabled).length}/{report.sections.length} sections
                      </span>
                      {expandedReports[availableType.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-gray-700/50 text-gray-500 rounded">
                      Non configure
                    </span>
                  )}
                </div>
              </button>

              {/* Report Sections */}
              {report && report.sections && expandedReports[availableType.id] && (
                <div className="border-t border-gray-700 p-4 space-y-3">
                  {report.sections.map(section => (
                    <div
                      key={section.id}
                      className={`p-4 rounded-lg border transition-colors ${
                        section.enabled
                          ? 'border-brand-green/30 bg-brand-green/5'
                          : 'border-gray-700 bg-gray-800/30 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <button
                              onClick={() => toggleSection(report.id, section.id)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                section.enabled ? 'bg-brand-green' : 'bg-gray-600'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  section.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <h5 className="font-medium text-white">{section.name}</h5>
                          </div>

                          {editingSection === `${report.id}-${section.id}` ? (
                            <div className="space-y-2">
                              <textarea
                                value={section.content}
                                onChange={(e) => updateSection(report.id, section.id, { content: e.target.value })}
                                className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white text-sm resize-none focus:border-brand-green"
                                rows={4}
                              />
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={async () => {
                                    setEditingSection(null);
                                    // Auto-save when finishing edit
                                    if (hasChanges) {
                                      await saveReportTypes();
                                    }
                                  }}
                                  disabled={saving}
                                  className="px-3 py-1 bg-brand-green text-brand-black rounded text-sm font-medium flex items-center gap-2"
                                >
                                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                  Sauvegarder
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">{section.content}</p>
                          )}
                        </div>

                        <button
                          onClick={() => setEditingSection(editingSection === `${report.id}-${section.id}` ? null : `${report.id}-${section.id}`)}
                          className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
          })}
        </div>
      </div>

      {/* Global Save Button - Always visible */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Save className="w-5 h-5 text-brand-green" />
            <div>
              <span className="text-white font-medium">Sauvegarder les modifications</span>
              {hasChanges && (
                <span className="ml-2 text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                  Modifications non sauvegardees
                </span>
              )}
            </div>
          </div>
          <button
            onClick={saveReportTypes}
            disabled={saving || !hasChanges}
            className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              hasChanges
                ? 'bg-brand-green text-brand-black hover:bg-brand-green/80'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-brand-green mt-0.5">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div className="text-sm text-gray-300">
            <div className="font-medium text-brand-green mb-1">Configuration des Rapports</div>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Les clients peuvent être vérifiés via l'API Pappers (données officielles)</li>
              <li>Le logo client sera automatiquement inclus dans les rapports PDF</li>
              <li>Activez/désactivez les sections selon les besoins du rapport</li>
              <li>Modifiez le contenu par défaut de chaque section selon vos templates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsConfig;
